import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { targetingLabel as tv, currentLocale } from '../i18n';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useAdvertiser } from '../contexts/AdvertiserContext';
import ChooseClientPlaceholder from './ChooseClientPlaceholder';
import AdvancedTargeting from './AdvancedTargeting';
import ResearchDashboard from './ResearchDashboard';
import { extractErrorMessage } from '../utils/errorMessage';
import '../styles/research.css';

interface GeoLocation {
  id: string;
  name: string;
  type: string;
}

interface AdvancedTargetingItem {
  id: number;
  name: string;
  full_path: string;
  google_audience_id: string | null;
  google_audience_type: string | null;
  type?: string;
}

interface ResearchAnswer {
  id?: number;
  position: number;
  answer_text: string;
  answer_image?: string;   // creative_test: uploaded option image URL
}

type QuestionType = 'single_choice' | 'multiple_choice' | 'creative_test' | 'creative_display';

interface ResearchQuestion {
  id?: number;
  position: number;
  question_text: string;
  question_type: QuestionType;
  is_required: boolean;
  answers: ResearchAnswer[];
  // creative_display (continuous only): a shown creative — image or video — no options.
  media_url?: string | null;
  media_type?: 'image' | 'video' | null;
  media_description?: string | null;
}

const CREATIVE_TEST_MAX = 4;
// A question_type that is NOT an answered question (no distribution, not priced/timed).
const isAnswered = (t: QuestionType) => t !== 'creative_display';

interface Research {
  id: number;
  advertiser_id: string;
  name: string;
  description: string | null;
  delivery?: 'random' | 'continuous';
  boost_speed?: boolean;
  respondent_count: number;
  status: string;
  targeting_geo: any[] | null;
  targeting_demographics: {
    age_ranges?: string[];
    genders?: string[];
    parental_statuses?: string[];
    household_income?: string[];
  } | null;
  targeting_audiences: any[] | null;
  questions: ResearchQuestion[];
  questions_count?: number;
  created_at: string;
  updated_at: string;
  price?: number | null;
  currency?: string | null;
  price_configured?: boolean;
}

const formatPrice = (price: number | null | undefined, currency: string | null | undefined): string => {
  if (price === null || price === undefined) return '—';
  const num = Math.round(price);  // whole number — no decimals
  // Group thousands with non-breaking space; keep up to 2 decimals only when needed.
  const parts = [String(num)];
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `${parts.join('.')}${currency ? ' ' + currency : ''}`;
};

const RESPONDENT_OPTIONS = [
  { value: 1000, label: '1,000' },
  { value: 2500, label: '2,500' },
  { value: 5000, label: '5,000' },
  { value: 10000, label: '10,000' },
];

// Continuous caps the sample at 1,000 with smaller tiers (sequential scenario).
const CONTINUOUS_RESPONDENT_OPTIONS = [
  { value: 100, label: '100' },
  { value: 250, label: '250' },
  { value: 500, label: '500' },
  { value: 1000, label: '1,000' },
];

const AGE_RANGES = [
  { value: 'AGE_RANGE_18_24', label: '18-24' },
  { value: 'AGE_RANGE_25_34', label: '25-34' },
  { value: 'AGE_RANGE_35_44', label: '35-44' },
  { value: 'AGE_RANGE_45_54', label: '45-54' },
  { value: 'AGE_RANGE_55_64', label: '55-64' },
  { value: 'AGE_RANGE_65_PLUS', label: '65+' },
];

const GENDERS = [
  { value: 'GENDER_MALE', label: 'Male' },
  { value: 'GENDER_FEMALE', label: 'Female' },
];

const PARENTAL_STATUSES = [
  { value: 'PARENTAL_STATUS_PARENT', label: 'Parent' },
  { value: 'PARENTAL_STATUS_NOT_A_PARENT', label: 'Not a Parent' },
];

const HOUSEHOLD_INCOME = [
  { value: 'top10', label: 'Very High' },
  { value: 'top20', label: 'High' },
  { value: 'top30', label: 'Above Average' },
  { value: 'top40', label: 'Average' },
  { value: 'top50', label: 'Below Average' },
  { value: 'lower50', label: 'Low' },
];

const ResearchPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAnalyst = user?.role === 'analyst' || user?.role === 'agency_analyst';
  const { selectedAdvertiser, isAllClients } = useAdvertiser();
  const selectedAdvertiserId = selectedAdvertiser?.advertiser_id || '';
  const [researchList, setResearchList] = useState<Research[]>([]);
  const [selectedResearch, setSelectedResearch] = useState<Research | null>(null);
  const [duplicatingId, setDuplicatingId] = useState<number | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Form state
  const [researchName, setResearchName] = useState('');
  const [delivery, setDelivery] = useState<'random' | 'continuous'>('random');
  const [boostSpeed, setBoostSpeed] = useState(false);
  const [respondentCount, setRespondentCount] = useState(1000);
  const [questions, setQuestions] = useState<ResearchQuestion[]>([]);

  // Count of ANSWERED questions (creative_display steps are free / not timed).
  const answeredCount = questions.filter(q => isAnswered(q.question_type)).length;
  const respondentOptions = delivery === 'continuous' ? CONTINUOUS_RESPONDENT_OPTIONS : RESPONDENT_OPTIONS;

  // Live price preview for the form
  const [pricePreview, setPricePreview] = useState<{
    price: number | null;
    earn_price?: number | null;
    currency: string | null;
    configured: boolean;
    account_balance?: number;
    account_currency?: string | null;
    billing_model?: 'prepaid' | 'postpaid' | string;
    sufficient_balance?: boolean | null;
  } | null>(null);

  // Targeting state
  const [targetingGeo, setTargetingGeo] = useState<GeoLocation[]>([]);
  const [targetingDemographics, setTargetingDemographics] = useState<{
    age_ranges: string[];
    genders: string[];
    parental_statuses: string[];
    household_income: string[];
  }>({ age_ranges: [], genders: [], parental_statuses: [], household_income: [] });
  const [targetingAudiences, setTargetingAudiences] = useState<AdvancedTargetingItem[]>([]);

  // Geo search state
  const [geoSearchQuery, setGeoSearchQuery] = useState('');
  const [geoSearchResults, setGeoSearchResults] = useState<GeoLocation[]>([]);
  const [geoSearchLoading, setGeoSearchLoading] = useState(false);
  const [showGeoDropdown, setShowGeoDropdown] = useState(false);
  const geoSearchRef = useRef<HTMLDivElement>(null);
  const geoSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showLaunchModal, setShowLaunchModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (selectedAdvertiserId) {
      loadResearchList();
      setSelectedResearch(null);
      setIsCreating(false);
    }
  }, [selectedAdvertiserId]);

  // Esc key handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showDeleteModal && !isDeleting) {
        setShowDeleteModal(false);
      }
      if (e.key === 'Escape' && showLaunchModal && !saving) {
        setShowLaunchModal(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showDeleteModal, isDeleting, showLaunchModal, saving]);

  // Close geo dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (geoSearchRef.current && !geoSearchRef.current.contains(event.target as Node)) {
        setShowGeoDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Live pricing preview — re-fetches when respondent count, questions
  // count, or selected advertiser changes (the matrix is per-office,
  // resolved via the advertiser).
  useEffect(() => {
    const qCount = answeredCount;
    if (!selectedAdvertiserId || qCount < 1) {
      setPricePreview(null);
      return;
    }
    let cancelled = false;
    const url = `/api/sense/research/pricing/preview?advertiser_id=${encodeURIComponent(selectedAdvertiserId)}&respondent_count=${respondentCount}&questions_count=${qCount}&delivery=${delivery}&boost_speed=${boostSpeed}`;
    api.get(url)
      .then((res: any) => {
        if (cancelled) return;
        const data = res?.data ?? res;
        setPricePreview({
          price: data?.price ?? null,
          earn_price: data?.earn_price ?? null,
          currency: data?.currency ?? null,
          configured: !!data?.configured,
          account_balance: data?.account_balance,
          account_currency: data?.account_currency,
          billing_model: data?.billing_model,
          sufficient_balance: data?.sufficient_balance,
        });
      })
      .catch(() => { if (!cancelled) setPricePreview(null); });
    return () => { cancelled = true; };
  }, [selectedAdvertiserId, respondentCount, answeredCount, delivery, boostSpeed]);

  // Geo search with debounce
  useEffect(() => {
    if (geoSearchTimeoutRef.current) {
      clearTimeout(geoSearchTimeoutRef.current);
    }
    if (geoSearchQuery.length >= 2) {
      setGeoSearchLoading(true);
      geoSearchTimeoutRef.current = setTimeout(() => {
        searchGeo(geoSearchQuery);
      }, 300);
    } else {
      setGeoSearchResults([]);
      setGeoSearchLoading(false);
    }
    return () => {
      if (geoSearchTimeoutRef.current) clearTimeout(geoSearchTimeoutRef.current);
    };
  }, [geoSearchQuery]);

  const searchGeo = async (query: string) => {
    if (!selectedAdvertiserId) return;
    try {
      const response = await api.get(
        `/api/targeting/geo/search?q=${encodeURIComponent(query)}&advertiser_id=${selectedAdvertiserId}&limit=10`
      );
      setGeoSearchResults(response.data.results || []);
      setShowGeoDropdown(true);
    } catch (err) {
      console.error('Geo search error:', err);
      setGeoSearchResults([]);
    } finally {
      setGeoSearchLoading(false);
    }
  };

  const addGeoLocation = (geo: GeoLocation) => {
    if (!targetingGeo.find(g => g.id === geo.id)) {
      setTargetingGeo([...targetingGeo, geo]);
    }
    setGeoSearchQuery('');
    setGeoSearchResults([]);
    setShowGeoDropdown(false);
  };

  const removeGeoLocation = (geoId: string) => {
    setTargetingGeo(targetingGeo.filter(g => g.id !== geoId));
  };

  const loadResearchList = async () => {
    if (!selectedAdvertiserId) return;
    setLoading(true);
    try {
      const response = await api.get(
        `/api/sense/research?advertiser_id=${selectedAdvertiserId}&status=active`
      );
      setResearchList(response.data.research || []);
    } catch (err: any) {
      console.error('Error loading research:', err);
      setResearchList([]);
    } finally {
      setLoading(false);
    }
  };

  const loadResearchDetails = async (researchId: number) => {
    try {
      const response = await api.get(`/api/sense/research/${researchId}`);
      return response.data;
    } catch (err) {
      console.error('Error loading research details:', err);
      return null;
    }
  };

  const handleSelectResearch = async (research: Research) => {
    // Load full details
    const details = await loadResearchDetails(research.id);
    if (details) {
      setSelectedResearch(details);
      setIsCreating(false);
      setResearchName(details.name);
      setDelivery((details.delivery as any) || 'random');
      setBoostSpeed(!!details.boost_speed);
      setRespondentCount(details.respondent_count);
      setQuestions(details.questions || []);
      setTargetingGeo(details.targeting_geo || []);
      setTargetingDemographics(details.targeting_demographics || {
        age_ranges: [], genders: [], parental_statuses: [], household_income: []
      });
      setTargetingAudiences(details.targeting_audiences || []);
      setError('');
      setSuccess('');
    }
  };

  const handleCreateNew = () => {
    setSelectedResearch(null);
    setIsCreating(true);
    setResearchName('');
    setDelivery('random');
    setBoostSpeed(false);
    setRespondentCount(1000);
    setQuestions([{
      position: 1,
      question_text: '',
      question_type: 'single_choice',
      is_required: true,
      answers: [
        { position: 1, answer_text: '' },
        { position: 2, answer_text: '' }
      ]
    }]);
    setTargetingGeo([]);
    setTargetingDemographics({ age_ranges: [], genders: [], parental_statuses: [], household_income: [] });
    setTargetingAudiences([]);
    setError('');
    setSuccess('');
  };

  const handleSave = async () => {
    if (!researchName.trim()) {
      setError(t('research.enterName'));
      return;
    }

    // Validate questions
    for (const q of questions) {
      if (q.question_type === 'creative_display') {
        if (!q.media_url) {
          setError(t('research.creativeStepNeedsMedia'));
          return;
        }
        continue;  // a shown creative has no text/answers
      }
      if (!q.question_text.trim()) {
        setError(t('research.questionsNeedText'));
        return;
      }
      if (q.answers.length < 2) {
        setError(t('research.questionNeeds2Answers'));
        return;
      }
      if (q.question_type === 'creative_test') {
        if (q.answers.length > CREATIVE_TEST_MAX) {
          setError(t('research.creativeTestMaxImages', { max: CREATIVE_TEST_MAX }));
          return;
        }
        for (const a of q.answers) {
          if (!a.answer_image) {
            setError(t('research.creativeOptionNeedsImage'));
            return;
          }
        }
      } else {
        for (const a of q.answers) {
          if (!a.answer_text.trim()) {
            setError(t('research.answersNeedText'));
            return;
          }
        }
      }
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const payload = {
        advertiser_id: selectedAdvertiserId,
        name: researchName.trim(),
        delivery,
        boost_speed: boostSpeed,
        respondent_count: respondentCount,
        targeting_geo: targetingGeo.length > 0 ? targetingGeo : null,
        targeting_demographics: Object.values(targetingDemographics).some(arr => arr.length > 0)
          ? targetingDemographics : null,
        targeting_audiences: targetingAudiences.length > 0 ? targetingAudiences : null,
        // position follows array order so interleaved creative steps keep their place.
        questions: questions.map((q, i) => ({
          position: i + 1,
          question_text: q.question_type === 'creative_display' ? (q.question_text || '') : q.question_text,
          question_type: q.question_type,
          is_required: q.is_required,
          media_url: q.question_type === 'creative_display' ? (q.media_url || null) : null,
          media_type: q.question_type === 'creative_display' ? (q.media_type || null) : null,
          media_description: q.question_type === 'creative_display' ? (q.media_description || null) : null,
          answers: q.question_type === 'creative_display' ? [] : q.answers.map((a, j) => ({
            position: j + 1,
            answer_text: q.question_type === 'creative_test' ? '' : a.answer_text,
            answer_image: q.question_type === 'creative_test' ? (a.answer_image || null) : null,
          }))
        }))
      };

      if (isCreating) {
        const response = await api.post(`/api/sense/research`, payload);
        setSuccess(`Research "${researchName}" created successfully`);
        await loadResearchList();
        // Select the newly created research
        const newResearch = response.data;
        if (newResearch) {
          setSelectedResearch(newResearch);
          setIsCreating(false);
        }
      } else if (selectedResearch) {
        await api.patch(`/api/sense/research/${selectedResearch.id}`, payload);
        setSuccess(t('research.updatedOk'));
        await loadResearchList();
        // Reload details
        const updated = await loadResearchDetails(selectedResearch.id);
        if (updated) setSelectedResearch(updated);
      }
    } catch (err: any) {
      setError(extractErrorMessage(err, t('research.saveError')));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = () => {
    if (!selectedResearch) return;
    setShowDeleteModal(true);
  };

  const handleLaunchClick = () => {
    if (!selectedResearch) return;
    setShowLaunchModal(true);
  };

  const handleConfirmLaunch = async () => {
    if (!selectedResearch) return;
    setSaving(true);
    setError('');
    try {
      await api.post(`/api/sense/research/${selectedResearch.id}/launch`);
      setShowLaunchModal(false);
      setSuccess(t('research.launchedOk'));
      await loadResearchList();
      const updated = await loadResearchDetails(selectedResearch.id);
      if (updated) setSelectedResearch(updated);
    } catch (err: any) {
      setError(extractErrorMessage(err, t('research.launchError')));
    } finally {
      setSaving(false);
    }
  };

  const handlePause = async () => {
    if (!selectedResearch) return;
    setSaving(true);
    setError('');
    try {
      // Add minimum delay for better UX
      const [response] = await Promise.all([
        api.post(`/api/sense/research/${selectedResearch.id}/pause`),
        new Promise(resolve => setTimeout(resolve, 1500))
      ]);
      setSuccess(t('research.pausedOk'));
      await loadResearchList();
      const updated = await loadResearchDetails(selectedResearch.id);
      if (updated) setSelectedResearch(updated);
    } catch (err: any) {
      setError(extractErrorMessage(err, t('research.pauseError')));
    } finally {
      setSaving(false);
    }
  };

  const handleResume = async () => {
    if (!selectedResearch) return;
    setSaving(true);
    setError('');
    try {
      // Add minimum delay for better UX
      const [response] = await Promise.all([
        api.post(`/api/sense/research/${selectedResearch.id}/resume`),
        new Promise(resolve => setTimeout(resolve, 1500))
      ]);
      setSuccess(t('research.resumedOk'));
      await loadResearchList();
      const updated = await loadResearchDetails(selectedResearch.id);
      if (updated) setSelectedResearch(updated);
    } catch (err: any) {
      setError(extractErrorMessage(err, t('research.resumeError')));
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedResearch) return;

    setIsDeleting(true);
    try {
      await api.delete(`/api/sense/research/${selectedResearch.id}`);
      setShowDeleteModal(false);
      setSuccess(t('research.archivedOk'));
      setSelectedResearch(null);
      setIsCreating(false);
      await loadResearchList();
    } catch (err: any) {
      setError(extractErrorMessage(err, t('research.archiveError')));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDuplicate = async (researchId?: number) => {
    const id = researchId ?? selectedResearch?.id;
    if (!id) return;
    setDuplicatingId(id);
    try {
      const response = await api.post(`/api/sense/research/${id}/duplicate`);
      setSuccess(t('research.duplicatedOk'));
      await loadResearchList();
      // Open the copy straight away — it is a draft waiting to be corrected.
      if (response.data) {
        handleSelectResearch(response.data);
      }
    } catch (err: any) {
      setError(extractErrorMessage(err, t('research.duplicateError')));
    } finally {
      setDuplicatingId(null);
    }
  };

  const handleCancel = () => {
    setSelectedResearch(null);
    setIsCreating(false);
    setResearchName('');
    setQuestions([]);
    setError('');
    setSuccess('');
  };

  // Question management
  const addQuestion = () => {
    if (answeredCount >= 10) return;  // only answered questions are capped at 10
    setQuestions([...questions, {
      position: questions.length + 1,
      question_text: '',
      question_type: 'single_choice',
      is_required: true,
      answers: [
        { position: 1, answer_text: '' },
        { position: 2, answer_text: '' }
      ]
    }]);
  };

  // Upload the shown creative for a creative_display step (image → upload-image,
  // video → upload-video which also extracts frames for the AI).
  const uploadQuestionMedia = async (index: number, file: File, kind: 'image' | 'video') => {
    try {
      const fd = new FormData();
      fd.append('file', file);
      const endpoint = kind === 'video' ? '/api/sense/research/upload-video' : '/api/sense/research/upload-image';
      const r = await api.post(endpoint, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = r.data?.url;
      if (!url) return;
      updateQuestion(index, { media_url: url, media_type: kind });
      if (kind === 'video' && r.data && r.data.frames_ok === false) {
        setError(t('research.framesNotExtracted'));
      }
    } catch (e: any) {
      setError(extractErrorMessage(e) || t('research.uploadCreativeError'));
    }
  };

  const removeQuestion = (index: number) => {
    setQuestions(questions.filter((_, i) => i !== index));
  };

  const updateQuestion = (index: number, updates: Partial<ResearchQuestion>) => {
    setQuestions(questions.map((q, i) => i === index ? { ...q, ...updates } : q));
  };

  // Switch a step's type via the toggle (single / multiple / creative_test / creative_display).
  //  • creative_display ("Creative break") = a shown creative, no options.
  //  • creative_test caps options at 4 and clears text.
  //  • switching to an answered type ensures at least 2 answer rows exist.
  const DEFAULT_ANSWERS = (): ResearchAnswer[] => ([{ position: 1, answer_text: '' }, { position: 2, answer_text: '' }]);
  const setQuestionType = (index: number, type: QuestionType) => {
    const q = questions[index];
    let answers = q.answers;
    if (type === 'creative_display') {
      answers = [];
    } else if (type === 'creative_test') {
      const base = q.answers.length ? q.answers : DEFAULT_ANSWERS();
      answers = base.slice(0, CREATIVE_TEST_MAX).map((a, i) => ({ ...a, position: i + 1, answer_text: '' }));
    } else {
      answers = q.answers.length >= 2 ? q.answers : DEFAULT_ANSWERS();
    }
    updateQuestion(index, { question_type: type, answers });
  };

  // Answer management
  const addAnswer = (questionIndex: number) => {
    const q = questions[questionIndex];
    const max = q.question_type === 'creative_test' ? CREATIVE_TEST_MAX : 10;
    if (q.answers.length >= max) return;
    updateQuestion(questionIndex, {
      answers: [...q.answers, { position: q.answers.length + 1, answer_text: '' }]
    });
  };

  // Upload a creative-test option image and store its URL on the answer.
  const uploadAnswerImage = async (questionIndex: number, answerIndex: number, file: File) => {
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/api/sense/research/upload-image', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = r.data?.url;
      if (!url) return;
      const q = questions[questionIndex];
      updateQuestion(questionIndex, {
        answers: q.answers.map((a, i) => i === answerIndex ? { ...a, answer_image: url } : a),
      });
    } catch (e: any) {
      setError(extractErrorMessage(e) || t('research.uploadImageError'));
    }
  };

  const removeAnswer = (questionIndex: number, answerIndex: number) => {
    const q = questions[questionIndex];
    if (q.answers.length <= 2) return;
    updateQuestion(questionIndex, {
      answers: q.answers.filter((_, i) => i !== answerIndex)
    });
  };

  const updateAnswer = (questionIndex: number, answerIndex: number, text: string) => {
    const q = questions[questionIndex];
    updateQuestion(questionIndex, {
      answers: q.answers.map((a, i) => i === answerIndex ? { ...a, answer_text: text } : a)
    });
  };

  // Demographics toggles
  const toggleDemographic = (type: keyof typeof targetingDemographics, value: string) => {
    const current = targetingDemographics[type];
    if (current.includes(value)) {
      setTargetingDemographics({
        ...targetingDemographics,
        [type]: current.filter(v => v !== value)
      });
    } else {
      setTargetingDemographics({
        ...targetingDemographics,
        [type]: [...current, value]
      });
    }
  };

  const filteredResearch = researchList.filter(r =>
    r.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'DRAFT': return <span className="status-badge status-draft">{t('research.statusDraft')}</span>;
      case 'ACTIVE': return <span className="status-badge status-active">{t('research.statusActive')}</span>;
      case 'PAUSED': return <span className="status-badge status-paused">{t('research.statusPaused')}</span>;
      case 'COMPLETED': return <span className="status-badge status-completed">{t('research.statusCompleted')}</span>;
      default: return <span className="status-badge">{status}</span>;
    }
  };

  const isEditable = !selectedResearch || selectedResearch.status === 'DRAFT';

  if (isAllClients) return <ChooseClientPlaceholder pageKey="nav.research" />;

  return (
    <>
      {/* Header */}
      <div className="header">
        <div className="header-title">
          <h1>{t('research.title')}</h1>
          <p className="header-subtitle">{t('research.subtitle')}</p>
        </div>
      </div>

      {/* Main Layout */}
      <div className="url-lists-layout">
        {/* Left Panel - Research List */}
        <aside className="url-lists-panel">
          {/* Search */}
          <div className="panel-section">
            <div className="search-wrapper">
              <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <input
                type="text"
                className="search-input"
                placeholder={t('research.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          {/* Add Button */}
          {!isAnalyst && (
          <button className="add-list-btn" onClick={handleCreateNew}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            <span>{t('research.newResearch')}</span>
          </button>
          )}

          {/* Research List */}
          <div className="lists-container">
            {loading ? (
              <div className="lists-loading">
                <div className="spinner"></div>
                <span>{t('research.loadingResearch')}</span>
              </div>
            ) : filteredResearch.length === 0 ? (
              <div className="lists-empty">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>{searchQuery ? t('research.noMatching') : t('research.noResearchYet')}</span>
              </div>
            ) : (
              filteredResearch.map(research => (
                <div
                  key={research.id}
                  className={`list-item ${selectedResearch?.id === research.id ? 'active' : ''}`}
                  onClick={() => handleSelectResearch(research)}
                >
                  <div className="list-item-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                      <path d="M9 14l2 2 4-4"/>
                    </svg>
                  </div>
                  <div className="list-item-content">
                    <div className="list-item-name">{research.name}</div>
                    <div className="list-item-meta">
                      {t('research.questionsCount', { count: research.questions_count || 0 })}
                    </div>
                    <div className="list-item-meta">
                      {t('research.respondentsCount', { count: research.respondent_count || 0, value: (research.respondent_count || 0).toLocaleString(currentLocale()) })}
                    </div>
                    <div className={`list-item-meta list-item-price ${research.price == null ? 'list-item-price-empty' : ''}`}>
                      {research.price == null
                        ? (research.price_configured === false ? t('research.priceNotConfigured') : '—')
                        : formatPrice(research.price, research.currency)}
                    </div>
                  </div>
                  {getStatusBadge(research.status)}
                  {/* Copying belongs to the list, not to the open study: you pick
                      the one to clone while looking at all of them. The row is
                      itself a button, so the click must not select as well. */}
                  <button
                    className="list-item-action"
                    title={t('research.duplicateHint')}
                    aria-label={`Duplicate ${research.name}`}
                    disabled={duplicatingId === research.id}
                    onClick={(e) => { e.stopPropagation(); handleDuplicate(research.id); }}
                  >
                    {duplicatingId === research.id ? (
                      <span className="list-item-action-spinner" />
                    ) : (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                      </svg>
                    )}
                  </button>
                </div>
              ))
            )}
          </div>
        </aside>

        {/* Right Panel - Editor or Dashboard */}
        <div className="url-editor-panel">
          {!selectedResearch && !isCreating ? (
            <div className="editor-empty">
              <div className="editor-empty-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
                  <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                </svg>
              </div>
              <h3>{t('research.selectResearch')}</h3>
              <p>{t('research.selectResearchHint')}</p>
              {!isAnalyst && (
              <button className="editor-empty-btn" onClick={handleCreateNew}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
                {t('research.createNewResearch')}
              </button>
              )}
            </div>
          ) : selectedResearch && selectedResearch.status !== 'DRAFT' ? (
            /* Show Dashboard for ACTIVE, PAUSED, or COMPLETED research */
            <ResearchDashboard
              research={selectedResearch}
              onPause={handlePause}
              onResume={handleResume}
              saving={saving}
            />
          ) : (
            <div className="editor-content">
              {/* Editor Header */}
              <div className="editor-header">
                <h2>{isCreating ? t('research.newResearch') : t('research.editResearch')}</h2>
                <div className="editor-header-right">
                  {(() => {
                    if (questions.length === 0) {
                      return (
                        <div className="research-price-chip research-price-chip-muted">
                          <span className="research-price-chip-label">{t('research.price')}</span>
                          <span className="research-price-chip-value">add questions</span>
                        </div>
                      );
                    }
                    if (!pricePreview) {
                      return (
                        <div className="research-price-chip research-price-chip-muted">
                          <span className="research-price-chip-label">{t('research.price')}</span>
                          <span className="research-price-chip-value">…</span>
                        </div>
                      );
                    }
                    if (!pricePreview.configured) {
                      return (
                        <div className="research-price-chip research-price-chip-muted">
                          <span className="research-price-chip-label">{t('research.price')}</span>
                          <span className="research-price-chip-value">not configured</span>
                        </div>
                      );
                    }
                    // Active state — split amount and currency for differentiated styling.
                    // Prices are shown rounded to a whole number (no decimals).
                    const num = Math.round(pricePreview.price ?? 0);
                    const amountStr = String(num).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
                    return (
                      <div className="research-price-chip">
                        <span className="research-price-chip-label">{t('research.price')}</span>
                        <span className="research-price-chip-value">
                          <span className="research-price-chip-amount">{amountStr}</span>
                          {pricePreview.currency && (
                            <span className="research-price-chip-currency">{pricePreview.currency}</span>
                          )}
                        </span>
                      </div>
                    );
                  })()}
                  {selectedResearch && getStatusBadge(selectedResearch.status)}
                </div>
              </div>

              {/* Alerts */}
              {error && (
                <div className="alert alert-error">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                  <span>{error}</span>
                  <button onClick={() => setError('')}>&times;</button>
                </div>
              )}
              {success && (
                <div className="alert alert-success">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" strokeLinecap="round" strokeLinejoin="round"></path>
                    <polyline points="22 4 12 14.01 9 11.01" strokeLinecap="round" strokeLinejoin="round"></polyline>
                  </svg>
                  <span>{success}</span>
                  <button onClick={() => setSuccess('')}>&times;</button>
                </div>
              )}

              {/* Form */}
              <div className="editor-form research-form">
                {/* Basic Info */}
                <div className="form-section">
                  <div className="form-row">
                    <div className="form-group form-group-two-thirds">
                      <label className="form-label">{t('research.researchName')}</label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder={t('research.namePlaceholder')}
                        value={researchName}
                        onChange={(e) => setResearchName(e.target.value)}
                        disabled={!isEditable}
                        maxLength={255}
                      />
                    </div>
                    <div className="form-group form-group-third">
                      <label className="form-label">{t('research.respondents')}</label>
                      <select
                        className="form-select"
                        value={respondentCount}
                        onChange={(e) => setRespondentCount(Number(e.target.value))}
                        disabled={!isEditable}
                      >
                        {respondentOptions.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Delivery mode + boost */}
                  <div className="form-row">
                    <div className="form-group form-group-third">
                      <label className="form-label">{t('research.delivery')}</label>
                      <select
                        className="form-select"
                        value={delivery}
                        onChange={(e) => {
                          const next = e.target.value as 'random' | 'continuous';
                          setDelivery(next);
                          // Clamp the sample to a tier valid for the new mode.
                          if (next === 'continuous') {
                            if (![100, 250, 500, 1000].includes(respondentCount)) setRespondentCount(1000);
                          } else {
                            if (respondentCount < 1000) setRespondentCount(1000);
                            // Creative shows only exist in continuous — drop them.
                            if (questions.some(q => q.question_type === 'creative_display')) {
                              setQuestions(questions.filter(q => q.question_type !== 'creative_display'));
                            }
                          }
                        }}
                        disabled={!isEditable}
                      >
                        <option value="random">{t('research.random')}</option>
                        <option value="continuous">{t('research.continuous')}</option>
                      </select>
                    </div>
                    <div className="form-group form-group-third">
                      <label className="form-label">{t('research.speed')}</label>
                      <div
                        role="switch"
                        aria-checked={boostSpeed}
                        onClick={() => isEditable && setBoostSpeed(!boostSpeed)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          height: 38,
                          cursor: isEditable ? 'pointer' : 'default',
                          opacity: isEditable ? 1 : 0.6,
                          userSelect: 'none',
                        }}
                      >
                        <span
                          style={{
                            position: 'relative',
                            width: 40,
                            height: 22,
                            borderRadius: 11,
                            flex: '0 0 auto',
                            background: boostSpeed ? 'linear-gradient(90deg,#FF6B4A,#E94B6C)' : '#d0d5dd',
                            transition: 'background .15s',
                          }}
                        >
                          <span
                            style={{
                              position: 'absolute',
                              top: 2,
                              left: boostSpeed ? 20 : 2,
                              width: 18,
                              height: 18,
                              borderRadius: '50%',
                              background: '#fff',
                              boxShadow: '0 1px 2px rgba(0,0,0,.25)',
                              transition: 'left .15s',
                            }}
                          />
                        </span>
                        <span style={{ fontSize: 14, color: '#344054', fontWeight: 500 }}>{t('research.boostSpeed')}</span>
                      </div>
                    </div>
                  </div>

                  <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                    {delivery === 'continuous'
                      ? t('research.deliveryHintContinuous')
                      : t('research.deliveryHintRandom')}
                  </p>

                </div>

                {/* Targeting Section */}
                <div className="form-section">
                  <h3 className="form-section-title">{t('research.targeting')}</h3>

                  {/* Geography */}
                  <div className="targeting-group">
                    <label className="targeting-label">{t('research.geography')}</label>
                    <div className="geo-email-container" ref={geoSearchRef}>
                      {targetingGeo.length > 0 && (
                        <div className="geo-email-list">
                          {targetingGeo.map(geo => (
                            <div key={geo.id} className="geo-email-item">
                              <span className="geo-email-name">{geo.name}</span>
                              {isEditable && (
                                <button
                                  type="button"
                                  className="geo-email-remove"
                                  onClick={() => removeGeoLocation(geo.id)}
                                >
                                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                  </svg>
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="geo-email-search">
                        <div className="geo-email-search-wrapper">
                          <input
                            type="text"
                            className="geo-email-input"
                            placeholder={t('research.searchLocations')}
                            value={geoSearchQuery}
                            onChange={(e) => setGeoSearchQuery(e.target.value)}
                            onFocus={() => geoSearchResults.length > 0 && setShowGeoDropdown(true)}
                            disabled={!isEditable}
                          />
                          {geoSearchLoading && <span className="geo-email-loading"></span>}
                        </div>
                        {showGeoDropdown && geoSearchResults.length > 0 && (
                          <div className="geo-email-dropdown">
                            {geoSearchResults.map(geo => (
                              <div
                                key={geo.id}
                                className="geo-email-dropdown-item"
                                onClick={() => addGeoLocation(geo)}
                              >
                                <span className="geo-dropdown-name">{geo.name}</span>
                                <span className="geo-dropdown-type">{geo.type}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        {showGeoDropdown && geoSearchQuery.length >= 2 && !geoSearchLoading && geoSearchResults.length === 0 && (
                          <div className="geo-email-dropdown">
                            <div className="geo-email-dropdown-empty">{t('research.noLocations')}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Age */}
                  <div className="targeting-group">
                    <div className="targeting-row">
                      <label className="targeting-label">{t('research.age')}</label>
                      <div className="targeting-tags">
                        {AGE_RANGES.map(opt => (
                          <span
                            key={opt.value}
                            className={`targeting-tag targeting-tag-toggle ${targetingDemographics.age_ranges.length === 0 || targetingDemographics.age_ranges.includes(opt.value) ? 'active' : ''}`}
                            onClick={() => isEditable && toggleDemographic('age_ranges', opt.value)}
                            style={{ cursor: isEditable ? 'pointer' : 'default', opacity: isEditable ? 1 : 0.7 }}
                          >
                            {opt.label}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Gender */}
                  <div className="targeting-group">
                    <div className="targeting-row">
                      <label className="targeting-label">{t('research.gender')}</label>
                      <div className="targeting-tags">
                        {GENDERS.map(opt => (
                          <span
                            key={opt.value}
                            className={`targeting-tag targeting-tag-toggle ${targetingDemographics.genders.length === 0 || targetingDemographics.genders.includes(opt.value) ? 'active' : ''}`}
                            onClick={() => isEditable && toggleDemographic('genders', opt.value)}
                            style={{ cursor: isEditable ? 'pointer' : 'default', opacity: isEditable ? 1 : 0.7 }}
                          >
                            {tv(opt.label)}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Parental Status */}
                  <div className="targeting-group">
                    <div className="targeting-row">
                      <label className="targeting-label">{t('research.parentalStatus')}</label>
                      <div className="targeting-tags">
                        {PARENTAL_STATUSES.map(opt => (
                          <span
                            key={opt.value}
                            className={`targeting-tag targeting-tag-toggle ${targetingDemographics.parental_statuses.length === 0 || targetingDemographics.parental_statuses.includes(opt.value) ? 'active' : ''}`}
                            onClick={() => isEditable && toggleDemographic('parental_statuses', opt.value)}
                            style={{ cursor: isEditable ? 'pointer' : 'default', opacity: isEditable ? 1 : 0.7 }}
                          >
                            {tv(opt.label)}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Household Income */}
                  <div className="targeting-group">
                    <div className="targeting-row">
                      <label className="targeting-label">{t('research.householdIncome')}</label>
                      <div className="targeting-tags">
                        {HOUSEHOLD_INCOME.map(opt => (
                          <span
                            key={opt.value}
                            className={`targeting-tag targeting-tag-toggle ${targetingDemographics.household_income.length === 0 || targetingDemographics.household_income.includes(opt.value) ? 'active' : ''}`}
                            onClick={() => isEditable && toggleDemographic('household_income', opt.value)}
                            style={{ cursor: isEditable ? 'pointer' : 'default', opacity: isEditable ? 1 : 0.7 }}
                          >
                            {tv(opt.label)}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Advanced Audiences */}
                  <div className="targeting-group">
                    <label className="targeting-label">{t('research.advancedAudiences')}</label>
                    <AdvancedTargeting
                      selectedItems={targetingAudiences}
                      onSelectionChange={setTargetingAudiences}
                      disabled={!isEditable}
                    />
                  </div>
                </div>

                {/* Questions Section */}
                <div className="form-section questions-section">
                  <h3 className="form-section-title">{t('research.questionsSection', { answered: answeredCount, max: 10 })}</h3>

                  <div className="questions-list">
                    {questions.map((q, qIndex) => (
                      <div key={qIndex} className={`question-card ${q.question_type === 'creative_display' ? 'question-card-creative' : ''}`}>
                        <div className="question-header">
                          <span className={`question-number ${q.question_type === 'creative_display' ? 'question-number-creative' : ''}`}>
                            {q.question_type === 'creative_display'
                              ? 'C'
                              : `Q${questions.slice(0, qIndex + 1).filter(x => isAnswered(x.question_type)).length}`}
                          </span>
                          <div className="question-type-toggle">
                            <button
                              type="button"
                              className={`type-btn ${q.question_type === 'single_choice' ? 'active' : ''}`}
                              onClick={() => setQuestionType(qIndex, 'single_choice')}
                              disabled={!isEditable}
                            >
                              {t('research.single')}
                            </button>
                            <button
                              type="button"
                              className={`type-btn ${q.question_type === 'multiple_choice' ? 'active' : ''}`}
                              onClick={() => setQuestionType(qIndex, 'multiple_choice')}
                              disabled={!isEditable}
                            >
                              {t('research.multiple')}
                            </button>
                            <button
                              type="button"
                              className={`type-btn ${q.question_type === 'creative_test' ? 'active' : ''}`}
                              onClick={() => setQuestionType(qIndex, 'creative_test')}
                              disabled={!isEditable}
                            >
                              {t('research.creativeTest')}
                            </button>
                            {delivery === 'continuous' && (
                              <button
                                type="button"
                                className={`type-btn ${q.question_type === 'creative_display' ? 'active' : ''}`}
                                onClick={() => setQuestionType(qIndex, 'creative_display')}
                                disabled={!isEditable}
                              >
                                {t('research.creativeBreak')}
                              </button>
                            )}
                          </div>
                          {questions.length > 1 && isEditable && (
                            <button
                              type="button"
                              className="remove-question-btn"
                              onClick={() => removeQuestion(qIndex)}
                            >
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                              </svg>
                            </button>
                          )}
                        </div>

                        {q.question_type === 'creative_display' ? (
                          <div className="creative-display-block">
                            {q.media_url ? (
                              q.media_type === 'video' ? (
                                <video
                                  src={`${(api.defaults.baseURL || '').replace(/\/$/, '')}${q.media_url}`}
                                  controls
                                  style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 8, background: '#000', display: 'block' }}
                                />
                              ) : (
                                <img
                                  src={`${(api.defaults.baseURL || '').replace(/\/$/, '')}${q.media_url}`}
                                  alt="creative"
                                  style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 8, display: 'block' }}
                                />
                              )
                            ) : (
                              <div style={{ height: 110, borderRadius: 8, background: '#f1f3f5', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#adb5bd', fontSize: 13 }}>
                                {t('research.noCreative')}
                              </div>
                            )}
                            {isEditable && (
                              <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap', justifyContent: 'flex-start' }}>
                                <label className="creative-upload-btn" style={{ flex: '0 0 auto', padding: '7px 14px' }}>
                                  {q.media_url && q.media_type === 'image' ? t('research.replaceImage') : t('research.uploadImage')}
                                  <input type="file" accept="image/png,image/jpeg,image/gif,image/webp" style={{ display: 'none' }}
                                    onChange={(e) => { const f = e.target.files && e.target.files[0]; if (f) uploadQuestionMedia(qIndex, f, 'image'); e.currentTarget.value = ''; }} />
                                </label>
                                <label className="creative-upload-btn" style={{ flex: '0 0 auto', padding: '7px 14px' }}>
                                  {q.media_url && q.media_type === 'video' ? t('research.replaceVideo') : t('research.uploadVideo')}
                                  <input type="file" accept="video/mp4,video/quicktime,video/webm" style={{ display: 'none' }}
                                    onChange={(e) => { const f = e.target.files && e.target.files[0]; if (f) uploadQuestionMedia(qIndex, f, 'video'); e.currentTarget.value = ''; }} />
                                </label>
                              </div>
                            )}
                            <input
                              type="text"
                              className="question-input"
                              placeholder={t('research.captionOptional')}
                              value={q.question_text}
                              onChange={(e) => updateQuestion(qIndex, { question_text: e.target.value })}
                              disabled={!isEditable}
                              maxLength={500}
                              style={{ marginTop: 8 }}
                            />
                          </div>
                        ) : (
                        <>
                        <input
                          type="text"
                          className="question-input"
                          placeholder={t('research.questionPlaceholder')}
                          value={q.question_text}
                          onChange={(e) => updateQuestion(qIndex, { question_text: e.target.value })}
                          disabled={!isEditable}
                          maxLength={500}
                        />

                        <div className="answers-list">
                          {q.question_type === 'creative_test' ? (
                            <div className="creative-options">
                              {q.answers.map((a, aIndex) => (
                                <div key={aIndex} className="creative-option">
                                  {a.answer_image ? (
                                    <div className="creative-thumb">
                                      <img src={`${(api.defaults.baseURL || '').replace(/\/$/, '')}${a.answer_image}`} alt={`Option ${aIndex + 1}`} />
                                    </div>
                                  ) : (
                                    <div className="creative-thumb creative-thumb-empty"><span>{t('research.noImage')}</span></div>
                                  )}
                                  {isEditable && (
                                    <div className="creative-option-actions">
                                      <label className="creative-upload-btn">
                                        {a.answer_image ? t('research.replace') : t('research.upload')}
                                        <input type="file" accept="image/png,image/jpeg,image/gif,image/webp" style={{ display: 'none' }}
                                          onChange={(e) => { const f = e.target.files && e.target.files[0]; if (f) uploadAnswerImage(qIndex, aIndex, f); e.currentTarget.value = ''; }} />
                                      </label>
                                      {q.answers.length > 2 && (
                                        <button type="button" className="remove-answer-btn" onClick={() => removeAnswer(qIndex, aIndex)}>
                                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                                        </button>
                                      )}
                                    </div>
                                  )}
                                </div>
                              ))}
                              {q.answers.length < CREATIVE_TEST_MAX && isEditable && (
                                <button type="button" className="add-answer-btn creative-add" onClick={() => addAnswer(qIndex)}>
                                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                                  {t('research.addImage')}
                                </button>
                              )}
                            </div>
                          ) : (
                            <>
                          {q.answers.map((a, aIndex) => (
                            <div key={aIndex} className="answer-row">
                              <span className="answer-marker">
                                {q.question_type === 'single_choice' ? (
                                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10"></circle>
                                  </svg>
                                ) : (
                                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <rect x="3" y="3" width="18" height="18" rx="2"></rect>
                                  </svg>
                                )}
                              </span>
                              <input
                                type="text"
                                className="answer-input"
                                placeholder={t('research.answerPlaceholder', { number: aIndex + 1 })}
                                value={a.answer_text}
                                onChange={(e) => updateAnswer(qIndex, aIndex, e.target.value)}
                                disabled={!isEditable}
                                maxLength={200}
                              />
                              {q.answers.length > 2 && isEditable && (
                                <button
                                  type="button"
                                  className="remove-answer-btn"
                                  onClick={() => removeAnswer(qIndex, aIndex)}
                                >
                                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                  </svg>
                                </button>
                              )}
                            </div>
                          ))}
                          {q.answers.length < 10 && isEditable && (
                            <button
                              type="button"
                              className="add-answer-btn"
                              onClick={() => addAnswer(qIndex)}
                            >
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="12" y1="5" x2="12" y2="19"></line>
                                <line x1="5" y1="12" x2="19" y2="12"></line>
                              </svg>
                              {t('research.addAnswer')}
                            </button>
                          )}
                            </>
                          )}
                        </div>
                        </>
                        )}
                      </div>
                    ))}
                  </div>

                  {answeredCount < 10 && isEditable && (
                    <button type="button" className="add-question-btn" onClick={addQuestion}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="12" y1="5" x2="12" y2="19"></line>
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                      </svg>
                      {t('research.addQuestion')}
                    </button>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="editor-actions">
                <div className="editor-actions-left">
                  {!isCreating && selectedResearch && (
                    <>
                      {!isAnalyst && isEditable && (
                        <button className="btn-danger" onClick={handleDeleteClick} disabled={saving}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3 6 5 6 21 6"></polyline>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                          </svg>
                          {t('research.archive')}
                        </button>
                      )}
                    </>
                  )}
                </div>
                <div className="editor-actions-right">
                  <button className="btn-secondary" onClick={handleCancel} disabled={saving}>
                    {t('research.cancel')}
                  </button>
                  {/* Pause button for ACTIVE research */}
                  {!isCreating && selectedResearch?.status === 'ACTIVE' && (
                    <button className="btn-warning" onClick={handlePause} disabled={saving}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="6" y="4" width="4" height="16"></rect>
                        <rect x="14" y="4" width="4" height="16"></rect>
                      </svg>
                      {t('research.pause')}
                    </button>
                  )}
                  {/* Resume button for PAUSED research */}
                  {!isCreating && selectedResearch?.status === 'PAUSED' && (
                    <button className="btn-success" onClick={handleResume} disabled={saving}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polygon points="5 3 19 12 5 21 5 3"></polygon>
                      </svg>
                      {t('research.resume')}
                    </button>
                  )}
                  {/* Save button for editable (DRAFT) */}
                  {isEditable && (
                    <button
                      className="btn-primary"
                      onClick={handleSave}
                      disabled={saving || !researchName.trim()}
                    >
                      {saving ? (
                        <>
                          <div className="btn-spinner"></div>
                          {t('research.saving')}
                        </>
                      ) : (
                        <>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                            <polyline points="17 21 17 13 7 13 7 21"></polyline>
                            <polyline points="7 3 7 8 15 8"></polyline>
                          </svg>
                          {isCreating ? t('research.createResearch') : t('research.saveChanges')}
                        </>
                      )}
                    </button>
                  )}
                  {/* Launch button for DRAFT research (not when creating) */}
                  {!isCreating && selectedResearch?.status === 'DRAFT' && (
                    <button className="btn-success" onClick={handleLaunchClick} disabled={saving}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polygon points="5 3 19 12 5 21 5 3"></polygon>
                      </svg>
                      {t('research.launch')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedResearch && (
        <div className="modal-overlay" onClick={() => !isDeleting && setShowDeleteModal(false)}>
          <div className="message-modal" onClick={(e) => e.stopPropagation()}>
            <div className="message-modal-icon delete">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6"/>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
              </svg>
            </div>
            <h3>{t('research.archiveQ')}</h3>
            <p>
              {t('research.aboutToArchive')} <strong>"{selectedResearch.name}"</strong>.<br/>
              {t('research.canUndoLater')}
            </p>
            <div className="message-modal-actions">
              <button
                className="message-modal-btn-secondary"
                onClick={() => setShowDeleteModal(false)}
                disabled={isDeleting}
              >
                {t('research.cancel')}
              </button>
              <button
                className="message-modal-btn-danger"
                onClick={handleConfirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <span className="btn-spinner"></span>
                    {t('research.archiving')}
                  </>
                ) : t('research.archive')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Launch Confirmation Modal */}
      {showLaunchModal && selectedResearch && (
        <div
          className="modal-overlay"
          onClick={() => !saving && setShowLaunchModal(false)}
          style={{ opacity: 1, visibility: 'visible', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <div className="message-modal" onClick={(e) => e.stopPropagation()}>
            <div className="message-modal-icon launch">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="5 3 19 12 5 21 5 3"/>
              </svg>
            </div>
            <h3>{t('research.launchQ')}</h3>
            <p>
              {t('research.aboutToLaunch')} <strong>"{selectedResearch.name}"</strong>.
            </p>
            {pricePreview && pricePreview.configured && pricePreview.price !== null ? (
              <>
                <div className="launch-charge-block">
                  <div className="launch-charge-label">{t('research.amountCharged')}</div>
                  <div className="launch-charge-value">
                    {formatPrice(pricePreview.price, pricePreview.currency)}
                  </div>
                  {pricePreview.account_balance !== undefined && (
                    <div className="launch-balance-line">
                      Current balance:{' '}
                      <strong>{formatPrice(pricePreview.account_balance, pricePreview.account_currency)}</strong>
                      {pricePreview.billing_model === 'postpaid' && ' · postpaid'}
                    </div>
                  )}
                </div>
                {pricePreview.billing_model === 'prepaid' && pricePreview.sufficient_balance === false ? (
                  <p className="launch-warning launch-warning-error">
                    <strong>{t('research.insufficientBalance')}</strong> {t('research.topUpBeforeLaunch')}
                  </p>
                ) : (
                  <p className="launch-warning">
                    {t('research.deductedFromBalance')}
                    <strong> {t('research.cannotUndo')}</strong>
                  </p>
                )}
              </>
            ) : (
              <p className="launch-warning">
                <strong>{t('research.pricingNotConfigured')}</strong> {t('research.setMatrixInOffice')}
              </p>
            )}
            <div className="message-modal-actions">
              <button
                className="message-modal-btn-secondary"
                onClick={() => setShowLaunchModal(false)}
                disabled={saving}
              >
                {t('research.cancel')}
              </button>
              <button
                className="message-modal-btn-success"
                onClick={handleConfirmLaunch}
                disabled={
                  saving ||
                  !pricePreview ||
                  !pricePreview.configured ||
                  pricePreview.price === null ||
                  (pricePreview.billing_model === 'prepaid' && pricePreview.sufficient_balance === false)
                }
              >
                {saving ? (
                  <>
                    <span className="btn-spinner"></span>
                    {t('research.launching')}
                  </>
                ) : t('research.launchAndCharge')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ResearchPage;
